import { CdkMenu } from '@angular/cdk/menu';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  linkedSignal,
  output,
  OutputEmitterRef,
  Signal,
  WritableSignal,
} from '@angular/core';

import { ExpanderContentComponent, MenuItemComponent } from '@libs/common/components';
import { NavItemModel } from '@libs/shared/models';
import { TrackingGoogleService } from '@libs/shared/tracking';

@Component({
  selector: 'ui-category-side-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ExpanderContentComponent,
    MenuItemComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './category-side-menu.component.html',
  styleUrl: './category-side-menu.component.scss',
  hostDirectives: [
    CdkMenu,
  ],
})
export class CategorySideMenuComponent {
  public navItems: InputSignal<readonly NavItemModel[]> = input.required();
  public selectedItemId: InputSignal<string | undefined> = input();
  public selectedItemChange: OutputEmitterRef<NavItemModel> = output();

  public selectedItem: Signal<NavItemModel | undefined> = computed(() => {
    return this.findNavItemById(this.selectedItemId(), this.navItems());
  });

  public currentSelectedItem: WritableSignal<NavItemModel | undefined> =
    linkedSignal((): NavItemModel | undefined => this.selectedItem());

  public expandedItems: Signal<readonly NavItemModel[]> = computed(() => {
    const expanded: NavItemModel[] = [];
    const current: NavItemModel | undefined = this.currentSelectedItem();

    if (!current) {
      return expanded;
    }

    let tmpItem: NavItemModel | undefined = current;

    while (tmpItem) {
      expanded.unshift(tmpItem);
      tmpItem = tmpItem.parent;
    }

    return expanded;
  });

  private readonly tracking: TrackingGoogleService = inject(TrackingGoogleService);

  public setSelectedItem(item: NavItemModel): void {
    this.currentSelectedItem.set(item);
    this.computeTrackingRoute(item);
    this.selectedItemChange.emit(item);
  }

  public isActive(item: NavItemModel): boolean {
    const current: NavItemModel | undefined = this.currentSelectedItem();

    return !!current && item.id?.toString() === current.id?.toString();
  }

  public isExpanded(item: NavItemModel): boolean {
    return this.expandedItems().some((exp) => exp.id?.toString() === item.id?.toString());
  }

  private computeTrackingRoute(item: NavItemModel): void {
    const trackingRoute: string[] = [];
    let navItem: NavItemModel | undefined = item;

    while (navItem) {
      trackingRoute.push(navItem.label as string);
      navItem = navItem.parent;
    }

    const navCat: string = trackingRoute.reverse().join('_').toLowerCase()
      .replaceAll(' ', '-')
      .replaceAll('%', '');

    this.tracking.trackSidebarNavigationEvent(navCat);
  }

  private findNavItemById(id: string | undefined, items: readonly NavItemModel[]): NavItemModel | undefined {
    if (!id || items.length < 1) {
      return undefined;
    }

    for (const item of items) {
      if (item.id?.toString() === id?.toString()) {
        return item;
      }

      if (item.children?.length) {
        const childItem: NavItemModel | undefined = this.findNavItemById(id, item.children);

        if (childItem) {
          return childItem;
        }
      }
    }

    return undefined;
  }
}
